import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import { Edit, GripVertical, Save } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchBonuses, toggleBonusStatus, updateBonusImage } from 'store/bonusesSlice';
import { bonusService } from 'components/api/bonuses';
import { showToast } from 'store/toastSlice';
import CreateBonus from 'components/createBonus/createBonus';
import ImageUploader from 'components/imageUploader/imageUploader';
import styles from './BonusesList.module.scss';
import { Bonus } from 'types/bonus';

interface BonusesListProps {
  siteId: string;
}

interface SortableRowProps {
  bonus: Bonus;
  onToggle: (e: React.ChangeEvent<HTMLInputElement>, bonus: Bonus) => void;
  onEdit: (bonus: Bonus) => void;
  onEditImage: (bonus: Bonus) => void;
}

const SortableBonusRow: React.FC<SortableRowProps> = ({ bonus, onToggle, onEdit, onEditImage }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: bonus.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} className={styles.rowCard}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, flexWrap: 'wrap' }}>
        <Box {...attributes} {...listeners} className={styles.dragHandle}>
          <GripVertical size={20} />
        </Box>
        <Switch
          checked={bonus.isEnabled ?? false}
          onChange={(e) => onToggle(e, bonus)}
          size="small"
        />
        <Typography className={styles.idText}>ID: {bonus.id}</Typography>
        <Typography className={styles.titleText}>{bonus.info?.title || bonus.title}</Typography>
        {((bonus.translations && Object.keys(bonus.translations).length > 0) ||
          (bonus.info?.translations && Object.keys(bonus.info.translations).length > 0)) && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {Object.keys(bonus.translations || bonus.info?.translations || {}).map((lang) => (
              <Typography
                key={lang}
                component="span"
                sx={{
                  fontSize: '10px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  px: '6px',
                  py: '1px',
                  borderRadius: '4px',
                  bgcolor: 'rgba(0, 179, 245, 0.12)',
                  color: 'var(--accent-blue)',
                  border: '1px solid rgba(0, 179, 245, 0.25)',
                  letterSpacing: '0.5px',
                }}
                title={`Translation for ${lang.toUpperCase()}: ${
                  (bonus.translations || bonus.info?.translations)?.[lang]?.title || ''
                }`}
              >
                {lang}
              </Typography>
            ))}
          </Box>
        )}
        <Typography className={styles.descText}>
          {bonus.description || bonus.info?.description || ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box className={styles.imageCell}>
          <img
            src={bonus.info?.imageUrl || ''}
            alt="bonusImage"
            width={40}
            height={40}
            style={{ borderRadius: '50%', objectFit: 'cover' }}
          />
          <IconButton
            size="small"
            onClick={() => onEditImage(bonus)}
            className={styles.imageEditBtn}
          >
            <Edit size={12} />
          </IconButton>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<Edit size={14} />}
          onClick={() => onEdit(bonus)}
          className={styles.editBtn}
        >
          Edit
        </Button>
      </Box>
    </Box>
  );
};

const BonusesList: React.FC<BonusesListProps> = ({ siteId }) => {
  const dispatch = useAppDispatch();
  const { data: storeBonuses, isLoading, params } = useAppSelector((state) => state.bonuses);
  const [bonuses, setBonuses] = useState<Bonus[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Modals state
  const [editingBonus, setEditingBonus] = useState<Bonus | null>(null);
  const [imageModal, setImageModal] = useState({ open: false, id: '' });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSavingImage, setIsSavingImage] = useState(false);

  useEffect(() => {
    dispatch(fetchBonuses({ ...params, siteId }));
  }, [dispatch, siteId, params]);

  useEffect(() => {
    if (storeBonuses) {
      setBonuses(storeBonuses);
      setHasChanges(false);
    }
  }, [storeBonuses]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = bonuses.findIndex((b) => b.id === active.id);
      const newIndex = bonuses.findIndex((b) => b.id === over.id);
      const newOrder = arrayMove(bonuses, oldIndex, newIndex);
      setBonuses(newOrder);
      setHasChanges(true);
    }
  };

  const handleSaveOrder = async () => {
    setIsSavingOrder(true);
    const ids = bonuses.map((b) => Number(b.id));

    try {
      await bonusService.reorderBonuses({ ids });
      setHasChanges(false);
      dispatch(showToast({ text: 'Bonus order saved successfully!', type: 'success' }));
      dispatch(fetchBonuses({ ...params, siteId }));
    } catch (error: any) {
      console.error('Failed to reorder bonuses:', error);
      dispatch(
        showToast({
          text: error.response?.data?.message || error.message || 'Failed to update order',
          type: 'error',
        }),
      );
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleToggleBonusStatus = async (e: React.ChangeEvent<HTMLInputElement>, bonus: Bonus) => {
    const isEnabled = e.target.checked;
    try {
      await dispatch(toggleBonusStatus({ id: bonus.id, isEnabled })).unwrap();
      dispatch(showToast({ text: 'Bonus status updated successfully', type: 'success' }));
    } catch (error: any) {
      dispatch(showToast({ text: error || 'Failed to toggle bonus status', type: 'error' }));
    }
  };

  const handleUpdateImage = async () => {
    if (!selectedImage || !imageModal.id) return;

    const bonus = bonuses.find((b) => String(b.id) === String(imageModal.id));
    if (!bonus) return;

    setIsSavingImage(true);
    try {
      const resultAction = await dispatch(
        updateBonusImage({
          id: imageModal.id,
          image: selectedImage,
          siteId: siteId,
          bonusType: bonus.bonusType || '',
        }),
      );
      if (updateBonusImage.fulfilled.match(resultAction)) {
        dispatch(showToast({ text: 'Image updated successfully', type: 'success' }));
        setImageModal({ open: false, id: '' });
        setSelectedImage(null);
        dispatch(fetchBonuses({ ...params, siteId }));
      } else {
        dispatch(
          showToast({
            text: (resultAction.payload as string) || 'Failed to update image',
            type: 'error',
          }),
        );
      }
    } catch (error) {
      dispatch(showToast({ text: 'An unexpected error occurred', type: 'error' }));
    } finally {
      setIsSavingImage(false);
    }
  };

  if (isLoading && bonuses.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          Bonuses table
        </Typography>
        {hasChanges && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save size={16} />}
            onClick={handleSaveOrder}
            disabled={isSavingOrder}
            sx={{
              backgroundColor: 'var(--accent-blue)',
              '&:hover': { backgroundColor: '#0092c7' },
            }}
          >
            {isSavingOrder ? 'Saving...' : 'Save Order'}
          </Button>
        )}
      </Box>

      <Box className={styles.listWrapper}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={bonuses.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <Box className={styles.listContainer}>
              {bonuses.map((bonus) => (
                <SortableBonusRow
                  key={bonus.id}
                  bonus={bonus}
                  onToggle={handleToggleBonusStatus}
                  onEdit={(b) => setEditingBonus(b)}
                  onEditImage={(b) => setImageModal({ open: true, id: String(b.id) })}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>

      {/* Edit Bonus Title / Description Modal */}
      {editingBonus && (
        <Dialog
          open={!!editingBonus}
          onClose={() => setEditingBonus(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: 'var(--bg-header)',
              borderRadius: '16px',
              border: '1px solid var(--border-color)',
            },
          }}
          disableRestoreFocus
        >
          <DialogContent sx={{ p: 0 }}>
            <CreateBonus
              siteId={siteId}
              initialData={editingBonus}
              onClose={() => {
                setEditingBonus(null);
                dispatch(fetchBonuses({ ...params, siteId }));
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Image Modal */}
      {imageModal.open && (
        <Dialog
          open={imageModal.open}
          onClose={() => {
            setImageModal({ open: false, id: '' });
            setSelectedImage(null);
          }}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: {
              backgroundColor: 'var(--bg-panel)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
            },
          }}
        >
          <DialogContent>
            <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
              Edit Bonus Image
            </Typography>
            <ImageUploader
              initialPreview={
                bonuses.find((b) => String(b.id) === String(imageModal.id))?.info?.imageUrl || ''
              }
              onImageSelect={(file) => setSelectedImage(file)}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setImageModal({ open: false, id: '' });
                  setSelectedImage(null);
                }}
                disabled={isSavingImage}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleUpdateImage}
                disabled={isSavingImage || !selectedImage}
                sx={{
                  backgroundColor: 'var(--accent-blue)',
                  '&:hover': { backgroundColor: '#0092c7' },
                }}
              >
                {isSavingImage ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default BonusesList;
